// ── FQL Structured Search · pure helpers ─────────────────────────────────
// Ported 1:1 from fql-search-core.jsx (the React-free portion). These own:
// completion-position editing (parse → mutate AST → serialize), did-you-mean
// edit-distance, and the tiny MQL matcher used for the live preview.
//
// FQL owns parsing / conversion / suggestion-query specs / diagnostics.
// Matching is case-sensitive / exact; wildcards (make:Hon*) are the explicit
// opt-in; the live preview runs interactive posture.

import { FqlUtilities } from '@frankai/fql'

const Fql = FqlUtilities

// ═══════════════════════════════════════════════════════════════════════
//  AST helpers — flatten / rebuild the top-level conjunction
// ═══════════════════════════════════════════════════════════════════════

export function flattenTop(ast) {
  const child = ast && ast.child
  if (!child) return []
  if (child.type === 'and') return child.children.slice()
  return [child]
}

export function buildQuery(conjuncts) {
  if (conjuncts.length === 0) return { type: 'query', child: null }
  if (conjuncts.length === 1) return { type: 'query', child: conjuncts[0] }
  return { type: 'query', child: { type: 'and', children: conjuncts } }
}

// A value that contains whitespace (or quoting-sensitive chars) can't be a bare
// `word` token — it must serialize as a quoted `phrase` (e.g. model:"3 Series").
// Wildcards stay words so the pattern survives.
const valueNode = (text) => {
  const s = String(text)
  if (/[*?]/.test(s)) return { type: 'word', text: s, wildcard: true }
  if (/[\s"():[\]{}]/.test(s)) return { type: 'phrase', text: s }
  return { type: 'word', text: s, wildcard: false }
}
const wordClause = (field, text) => ({ type: 'clause', field, value: valueNode(text) })

const enumClause = (field, values) =>
  values.length === 1
    ? wordClause(field, values[0])
    : { type: 'or', commaSugar: true, children: values.map((v) => wordClause(field, v)) }

const rangeClause = (field, lower, upper, incLo, incUp) => ({
  type: 'clause',
  field,
  value: {
    type: 'range',
    lower: lower == null || lower === '' ? null : { type: 'word', text: String(lower) },
    upper: upper == null || upper === '' ? null : { type: 'word', text: String(upper) },
    includeLower: incLo !== false,
    includeUpper: incUp !== false,
  },
})

// Classify ONE top-level conjunct into something the sidebar can reason about.
// `representable` flags whether a checkbox/range control owns it.
export function classifyConjunct(node, fieldDefs) {
  const fields = (fieldDefs && fieldDefs.fields) || {}
  if (node.type === 'clause' && node.field && fields[node.field]) {
    const v = node.value
    if (v.type === 'word' && v.wildcard) return { kind: 'wildcard', field: node.field, text: v.text, representable: false }
    if (v.type === 'word') return { kind: 'eq', field: node.field, values: [v.text], representable: true }
    // A quoted phrase is an exact multi-word value — representable as a checkbox
    // value, same as a word (e.g. model:"3 Series").
    if (v.type === 'phrase') return { kind: 'eq', field: node.field, values: [v.text], representable: true }
    if (v.type === 'range') {
      return {
        kind: 'range',
        field: node.field,
        lower: v.lower ? v.lower.text : null,
        upper: v.upper ? v.upper.text : null,
        includeLower: v.includeLower,
        includeUpper: v.includeUpper,
        representable: true,
      }
    }
  }
  // comma-sugared OR over a single known field → enum multi-select
  if (node.type === 'or' && node.commaSugar && node.children && node.children.length) {
    const f = node.children[0].field
    const same = node.children.every(
      (c) => c.type === 'clause' && c.field === f && ((c.value.type === 'word' && !c.value.wildcard) || c.value.type === 'phrase'),
    )
    if (f && fields[f] && same) {
      return { kind: 'eq', field: f, values: node.children.map((c) => c.value.text), representable: true }
    }
  }
  return { kind: 'advanced', field: node.field || null, representable: false }
}

// Build the per-field view the sidebar renders from (selected values + active
// range), plus the list of unrepresentable "advanced" conjuncts.
export function buildFieldView(ast, fieldDefs) {
  const tops = flattenTop(ast)
  const byField = {}
  const advanced = []
  tops.forEach((node, index) => {
    const c = classifyConjunct(node, fieldDefs)
    if (!c.representable) {
      advanced.push({ index, text: safeSerialize(node), field: c.field, kind: c.kind })
      return
    }
    const slot = byField[c.field] || (byField[c.field] = { values: new Set(), range: null })
    if (c.kind === 'eq') c.values.forEach((v) => slot.values.add(v))
    else if (c.kind === 'range') slot.range = c
  })
  return { byField, advanced }
}

export function safeSerialize(node) {
  try {
    return Fql.serialize(node.type === 'query' ? node : { type: 'query', child: node })
  } catch {
    return '‹unserializable›'
  }
}

// ── String-level mutations (parse → mutate → serialize) ────────────────
export function toggleValue(query, field, value, fieldDefs) {
  const ast = Fql.parse(query)
  const tops = flattenTop(ast)
  const kept = []
  const current = new Set()
  for (const node of tops) {
    const c = classifyConjunct(node, fieldDefs)
    if (c.representable && c.kind === 'eq' && c.field === field) {
      c.values.forEach((v) => current.add(v))
    } else {
      kept.push(node)
    }
  }
  if (current.has(value)) current.delete(value)
  else current.add(value)
  const values = [...current]
  if (values.length) kept.push(enumClause(field, values))
  return safeSerialize(buildQuery(kept))
}

export function setRange(query, field, range, fieldDefs) {
  const ast = Fql.parse(query)
  const tops = flattenTop(ast).filter((node) => {
    const c = classifyConjunct(node, fieldDefs)
    return !(c.representable && c.field === field && (c.kind === 'range' || c.kind === 'eq'))
  })
  const hasBound = (range.lower != null && range.lower !== '') || (range.upper != null && range.upper !== '')
  if (hasBound) tops.push(rangeClause(field, range.lower, range.upper, range.includeLower, range.includeUpper))
  return safeSerialize(buildQuery(tops))
}

export function clearField(query, field, fieldDefs) {
  const ast = Fql.parse(query)
  const tops = flattenTop(ast).filter((node) => classifyConjunct(node, fieldDefs).field !== field)
  return safeSerialize(buildQuery(tops))
}

// Single-value set/replace (boolean tri-state, objectId) — drops every existing
// conjunct on the field, then sets exactly one (or none).
export function setValue(query, field, value, fieldDefs) {
  const ast = Fql.parse(query)
  const tops = flattenTop(ast).filter((node) => classifyConjunct(node, fieldDefs).field !== field)
  if (value != null && value !== '') tops.push(wordClause(field, value))
  return safeSerialize(buildQuery(tops))
}

export function removeConjunct(query, index) {
  const ast = Fql.parse(query)
  const tops = flattenTop(ast).filter((_, i) => i !== index)
  return safeSerialize(buildQuery(tops))
}

// Raw splice — used by did-you-mean (edit by character span).
export function replaceSpan(query, start, end, text) {
  return query.slice(0, start) + text + query.slice(end)
}

// ═══════════════════════════════════════════════════════════════════════
//  Did-you-mean — component-owned edit-distance over FQL diagnostics
// ═══════════════════════════════════════════════════════════════════════
export function levenshtein(a, b) {
  a = a.toLowerCase()
  b = b.toLowerCase()
  const m = a.length
  const n = b.length
  if (!m) return n
  if (!n) return m
  let prev = Array.from({ length: n + 1 }, (_, i) => i)
  for (let i = 1; i <= m; i++) {
    const cur = [i]
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
    }
    prev = cur
  }
  return prev[n]
}

// `cap` overrides the default distance ceiling — callers repairing values
// against a PARTIAL sample (backend fields) pass a tighter cap to avoid
// second-guessing a valid-but-unsampled value. Distance is computed
// case-insensitively, but an EXACT (case-sensitive) hit means there is nothing
// to fix; a case-only mismatch (distance 0 after lowercasing, no exact hit) IS
// offered, since FQL matches case-sensitively.
export function bestMatch(term, candidates, cap) {
  if (candidates.some((c) => String(c) === term)) return null
  let best = null
  let bestD = Infinity
  for (const cand of candidates) {
    const d = levenshtein(term, String(cand))
    if (d < bestD) {
      bestD = d
      best = String(cand)
    }
  }
  const limit = cap != null ? cap : Math.max(2, Math.ceil(term.length / 2))
  return best && bestD <= limit ? { value: best, distance: bestD } : null
}

// Collect every clause node so we can locate the one a diagnostic spans.
function collectClauses(node, out) {
  if (!node || typeof node !== 'object') return out
  if (node.type === 'clause') out.push(node)
  if (node.child) collectClauses(node.child, out)
  if (node.children) node.children.forEach((c) => collectClauses(c, out))
  return out
}

// → [{ span, from, to, label, code }] — one chip per fixable diagnostic.
export function didYouMean(query, ast, diagnostics, fieldDefs, knownValues) {
  const fields = (fieldDefs && fieldDefs.fields) || {}
  const fieldNames = Object.keys(fields)
  const clauses = collectClauses(ast.child, [])
  const findClause = (span) =>
    clauses.find((c) => c.span && c.span.start === span.start && c.span.end === span.end) ||
    clauses.find((c) => c.span && c.span.start <= span.start && c.span.end >= span.end)
  const out = []
  const seen = new Set()
  for (const d of diagnostics) {
    if (!d.span) continue
    const key = d.code + ':' + d.span.start + ':' + d.span.end
    if (seen.has(key)) continue

    if (d.code === 'FQL-UNKNOWN-FIELD') {
      const clause = findClause(d.span)
      const fromField = clause ? clause.field : query.slice(d.span.start, d.span.end)
      const fm = bestMatch(fromField, fieldNames)
      if (!fm) continue
      // Also try to repair the value against that field's known set.
      let valuePart = ''
      let toValue = null
      if (clause && clause.value && clause.value.type === 'word') {
        valuePart = clause.value.text
        const known = (knownValues && knownValues[fm.value]) || (fields[fm.value] && fields[fm.value].values) || null
        if (known) {
          const vm = bestMatch(valuePart, known)
          if (vm) toValue = vm.value
        }
      }
      const to = `${fm.value}:${toValue || valuePart}`
      const span = clause ? clause.span : d.span
      out.push({ span, from: query.slice(span.start, span.end), to, label: to, code: d.code })
      seen.add(key)
    } else if (d.code === 'FQL-VALUE-NOT-IN-ENUM' || d.code === 'FQL-COERCION-FAILED') {
      const clause = findClause(d.span)
      if (!clause || !clause.value || clause.value.type !== 'word') continue
      const decl = fields[clause.field]
      const known = (decl && decl.values) || (knownValues && knownValues[clause.field]) || null
      if (!known) continue
      const vm = bestMatch(clause.value.text, known)
      if (!vm) continue
      const span = clause.value.span
      out.push({ span, from: query.slice(span.start, span.end), to: vm.value, label: `${clause.field}:${vm.value}`, code: d.code })
      seen.add(key)
    }
  }

  // Proactive value repair on string / backend-query fields. FQL emits NO
  // diagnostic for an unknown value here (it can't validate offline), so we
  // offer corrections straight from the injected knownValues sample. Because
  // that sample is partial, the cap is deliberately tight (1 edit, or 2 for
  // longer tokens) so a valid-but-unsampled value is never "corrected" to a
  // coincidental neighbor. Skips spans already repaired by a diagnostic.
  for (const clause of clauses) {
    if (!clause.field || !clause.value || clause.value.type !== 'word' || clause.value.wildcard) continue
    const decl = fields[clause.field]
    if (!decl || decl.type !== 'string') continue
    const known = (knownValues && knownValues[clause.field]) || null
    if (!known || !known.length) continue
    const span = clause.value.span
    if (!span) continue
    const key = 'value-string:' + span.start + ':' + span.end
    if (seen.has(key)) continue
    if (out.some((o) => o.span.start === span.start && o.span.end === span.end)) continue
    const cap = clause.value.text.length >= 6 ? 2 : 1
    const vm = bestMatch(clause.value.text, known, cap)
    if (!vm) continue
    out.push({ span, from: query.slice(span.start, span.end), to: vm.value, label: `${clause.field}:${vm.value}`, code: 'FQL-CLIENT-VALUE-REPAIR' })
    seen.add(key)
  }
  return out
}

// ═══════════════════════════════════════════════════════════════════════
//  Tiny MQL matcher — runs convert()'s interactive filter for the live
//  preview match-count. Supports exactly what convert emits.
// ═══════════════════════════════════════════════════════════════════════
function unwrap(v) {
  if (v && typeof v === 'object') {
    if ('$date' in v) return Date.parse(v.$date)
    if ('$oid' in v) return v.$oid
  }
  return v
}
// Dates ride on docs as ISO strings but on filters as $date-unwrapped ms; coerce
// both sides to a comparable number so date eq + range preview work.
function asNum(v) {
  if (typeof v === 'number') return v
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return Date.parse(v)
  return Number(v)
}
function scalarEq(a, b) {
  b = unwrap(b)
  if (typeof b === 'number') return asNum(a) === b
  return String(a) === String(b) // case-sensitive / exact
}
function cmp(a, b) {
  b = unwrap(b)
  if (typeof b === 'number') return asNum(a) - b
  return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0
}
function matchOp(value, op) {
  if (op === null || typeof op !== 'object' || Array.isArray(op)) return scalarEq(value, op)
  const keys = Object.keys(op)
  if ('$date' in op || '$oid' in op) return scalarEq(value, op)
  return keys.every((k) => {
    const t = op[k]
    switch (k) {
      case '$in':
        return t.some((x) => scalarEq(value, x))
      case '$nin':
        return !t.some((x) => scalarEq(value, x))
      case '$ne':
        return !scalarEq(value, t)
      case '$regex': {
        try {
          return new RegExp(t).test(String(value))
        } catch {
          return false
        }
      }
      case '$gte':
        return cmp(value, t) >= 0
      case '$gt':
        return cmp(value, t) > 0
      case '$lte':
        return cmp(value, t) <= 0
      case '$lt':
        return cmp(value, t) < 0
      case '$not':
        return !matchOp(value, t)
      default:
        return scalarEq(value, op)
    }
  })
}
export function matchDoc(doc, filter) {
  if (!filter || typeof filter !== 'object') return true
  return Object.keys(filter).every((k) => {
    if (k === '$and') return filter[k].every((f) => matchDoc(doc, f))
    if (k === '$or') return filter[k].some((f) => matchDoc(doc, f))
    if (k === '$nor') return !filter[k].some((f) => matchDoc(doc, f))
    // Mongo's array rule, so a row the server returned is not filtered out here:
    // a condition on `registeredDealerships.name` holds when any entry's name
    // meets it, and a negation holds only when no entry's name meets its inner
    // condition.
    const values = valuesAt(doc, k)
    const op = filter[k]
    return isNegation(op) ? values.every((v) => matchOp(v, op)) : values.some((v) => matchOp(v, op))
  })
}
function isNegation(op) {
  return op !== null && typeof op === 'object' && !Array.isArray(op) && ('$ne' in op || '$nin' in op || '$not' in op)
}
// Every value a dotted path reaches, stepping into each entry of an array on
// the way down and at the leaf. A missing path reaches one `undefined`.
export function valuesAt(doc, path) {
  let values = [doc]
  for (const p of path.split('.')) {
    values = values.flatMap((o) => (Array.isArray(o) ? o : [o])).map((o) => (o == null ? undefined : o[p]))
  }
  return values.flatMap((v) => (Array.isArray(v) ? v : [v]))
}

// The package's tested helper surface, as a single bag (matches the React
// `window.fqlSearchHelpers`).
export const fqlSearchHelpers = {
  flattenTop,
  buildQuery,
  classifyConjunct,
  buildFieldView,
  toggleValue,
  setRange,
  setValue,
  clearField,
  removeConjunct,
  replaceSpan,
  didYouMean,
  matchDoc,
  levenshtein,
  bestMatch,
  safeSerialize,
}
